// Avondplanning stap 2: gym morgen, Ja of Nee (B24, B28). The days of the rhythm stand there by themselves; here PK
// says otherwise. Nee: the week goal stays, the weekend asks to catch up. Ja on a day without gym: at a time of his
// choice, and when the week is full a planned gym day of this week goes off for it (Ruilen).
import { useState } from "react";
import { toast } from "sonner";
import { weekStart, weekTarget } from "@shared/routines";
import { addDays, displayDate, displayTime } from "@shared/time";
import { Button, cn } from "../components/ui";
import type { DayBlock, PlanEntry } from "../lib/db";
import { planGymOn } from "../lib/evening";
import { isPausedIn, useRhythm } from "../lib/rhythm";
import { skipRoutineEntry, useCompletions, useRoutines, useWeekRoutineEntries } from "../lib/routines";

export function GymChoice({
  date,
  today,
  gym,
  gymTime,
  skipped,
  blocks,
}: {
  date: string;
  today: string;
  gym: PlanEntry | undefined;
  gymTime: string | null;
  skipped: PlanEntry | undefined;
  blocks: DayBlock[];
}) {
  const routines = useRoutines();
  const completions = useCompletions();
  const week = useWeekRoutineEntries(date);
  const rhythm = useRhythm();
  const sport = blocks.find((b) => b.slot_kind === "sport" && !b.group_title);
  const [adding, setAdding] = useState(false);
  const [time, setTime] = useState(sport ? sport.start_local.slice(0, 5) : "08:00");
  const [swapId, setSwapId] = useState<string | null>(null);
  const routine = routines?.find((r) => r.weekly_target);
  if (!routine || !completions || !week) return null;

  const monday = weekStart(date);
  const sunday = addDays(monday, 6);
  const mine = week.filter((e) => e.routine_id === routine.id && e.status === "planned");
  const done = completions.filter((c) => c.routine_id === routine.id && !c.deleted_at && c.done_on >= monday && c.done_on <= sunday).length;
  // The same count as the server (routine.planOn): what is still to come this week.
  const ahead = mine.filter((e) => e.plan_date > today || e.plan_date >= date).length;
  const full = done + ahead >= weekTarget(routine, date, isPausedIn(rhythm));
  const candidates = mine.filter((e) => e.plan_date > today && e.plan_date !== date).sort((a, b) => (a.plan_date < b.plan_date ? -1 : 1));
  const swap = candidates.find((c) => c.id === swapId);
  const yes = !!gym;
  // A gym day of this week that went off (ruilen, or Nee earlier): after Nee here it can come back.
  // Only while the week has room: putting one back may not go over the goal (tester 5c bug 3).
  const givenUp =
    !gym && !adding && !full
      ? week.filter((e) => e.routine_id === routine.id && e.status === "skipped" && e.plan_date > today && e.plan_date !== date)
      : [];

  async function choose(value: boolean) {
    if (!value) {
      // Nee before confirming only closes the form (tester 5c bug 2); on a planned gym it skips it.
      if (adding) {
        setAdding(false);
        setSwapId(null);
        return;
      }
      if (gym?.status === "planned") await skipRoutineEntry(gym, true);
      return;
    }
    if (yes) return;
    // A skipped gym comes back at once when the week has room; in a full week only with a swap.
    if (skipped && !full) return skipRoutineEntry(skipped, false);
    setAdding(true);
  }

  async function plan() {
    if (full && !swap) return;
    if (skipped) await skipRoutineEntry(skipped, false, full ? swap : undefined);
    else await planGymOn(routine!, date, time, full ? swap : undefined);
    toast(
      full && swap
        ? `Gym geruild: ${displayDate(swap.plan_date)} vervalt`
        : skipped
          ? "Gym staat er weer"
          : `Gym ingepland om ${displayTime(time)}`,
    );
    setAdding(false);
    setSwapId(null);
  }

  return (
    <>
      <div role="radiogroup" aria-label="Gym" className="mt-1 grid grid-cols-2 gap-2">
        {(
          [
            [true, "Ja"],
            [false, "Nee"],
          ] as const
        ).map(([value, label]) => {
          const on = value ? yes || adding : !yes && !adding;
          return (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => void choose(value)}
              className={cn(
                "min-h-12 rounded-xl border-2 font-semibold",
                on ? "border-pine bg-pine text-paper" : "border-mist bg-paper text-ink",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      {gym && <p className="mt-2 font-semibold">{gymTime ? `Om ${displayTime(gymTime.slice(0, 5))}` : "Ingepland"}</p>}
      {!gym && skipped && !adding && (
        <p className="mt-2 text-sm text-ink-soft">
          Overgeslagen. Je weekdoel blijft staan; in het weekend komt de vraag of je wilt inhalen.
        </p>
      )}
      {givenUp.map((e) => (
        <div key={e.id} className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-ink-soft">Gym op {displayDate(e.plan_date)} staat overgeslagen.</p>
          <Button variant="secondary" onClick={() => void skipRoutineEntry(e, false)}>
            Toch gaan op {displayDate(e.plan_date).split(" ")[0]}
          </Button>
        </div>
      ))}
      {!gym && adding && (
        <div className="mt-3 flex flex-col gap-3">
          {!skipped && (
            <label className="flex flex-col gap-1">
              <span className="text-sm text-ink-soft">Hoe laat</span>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value || time)}
                className="min-h-12 w-36 rounded-xl border-2 border-mist bg-paper px-3"
              />
            </label>
          )}
          {full && candidates.length > 0 && (
            <fieldset>
              <legend className="mb-1 text-sm text-ink-soft">Je weekdoel is vol. Welke gymdag vervalt ervoor?</legend>
              <div role="radiogroup" aria-label="Ruilen met" className="flex flex-col gap-2">
                {candidates.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={swap?.id === c.id}
                    onClick={() => setSwapId(c.id)}
                    className={cn(
                      "min-h-12 rounded-xl border-2 px-3 text-left",
                      swap?.id === c.id ? "border-pine bg-paper font-semibold" : "border-mist bg-paper",
                    )}
                  >
                    {displayDate(c.plan_date)}
                  </button>
                ))}
              </div>
            </fieldset>
          )}
          {full && candidates.length === 0 && (
            <p className="text-sm text-ink-soft">Je weekdoel is al gehaald en er staat geen gymdag meer om te ruilen.</p>
          )}
          <Button onClick={() => void plan()} disabled={full && !swap}>
            {full ? "Ruilen en inplannen" : "Inplannen"}
          </Button>
        </div>
      )}
    </>
  );
}
